import { useState } from "react";
import type { Message } from "../storage/db.js";

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

interface Props {
  message: Message;
  onClose(): void;
  onReply(): void;
  onCopy(): void;
  onEdit(): void;
  onReact(emoji: string | null): void;
  onDeleteForMe(): void;
  onDeleteForEveryone(): void;
}

function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * WhatsApp-style options for one message, opened by long-pressing it (or
 * right-clicking / the ⌄ button on a computer): react, reply, copy, edit,
 * info, delete.
 */
export function MessageActions({
  message: m,
  onClose,
  onReply,
  onCopy,
  onEdit,
  onReact,
  onDeleteForMe,
  onDeleteForEveryone,
}: Props) {
  const [view, setView] = useState<"main" | "delete" | "info">("main");
  const own = m.dir === "out";
  const canReact = !m.deleted;
  const canReply = !m.deleted;
  const canCopy = !m.deleted && m.kind === "text" && m.text.length > 0;
  const canEdit = own && !m.deleted && m.kind === "text";
  const mine = m.reactions?.mine;

  return (
    <div className="sheet-layer" role="dialog" aria-modal="true" aria-label="Message options">
      <button className="sheet-backdrop" aria-label="Close" onClick={onClose} />
      <div className="sheet">
        {view === "main" && (
          <>
            {canReact && (
              <div className="sheet-reactions">
                {QUICK_REACTIONS.map((e) => (
                  <button
                    key={e}
                    className={e === mine ? "selected" : ""}
                    aria-label={e === mine ? `Remove ${e} reaction` : `React ${e}`}
                    onClick={() => onReact(e === mine ? null : e)}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
            <div className="sheet-list">
              {canReply && (
                <button onClick={onReply}>
                  <span className="menu-icon">↩️</span> Reply
                </button>
              )}
              {canCopy && (
                <button onClick={onCopy}>
                  <span className="menu-icon">📋</span> Copy
                </button>
              )}
              {canEdit && (
                <button onClick={onEdit}>
                  <span className="menu-icon">✏️</span> Edit
                </button>
              )}
              <button onClick={() => setView("info")}>
                <span className="menu-icon">ℹ️</span> Info
              </button>
              <button className="danger" onClick={() => setView("delete")}>
                <span className="menu-icon">🗑️</span> Delete
              </button>
            </div>
          </>
        )}

        {view === "delete" && (
          <div className="sheet-list">
            <div className="sheet-title">Delete message?</div>
            {own && !m.deleted && (
              <button className="danger" onClick={onDeleteForEveryone}>
                <span className="menu-icon">🗑️</span> Delete for everyone
              </button>
            )}
            <button className="danger" onClick={onDeleteForMe}>
              <span className="menu-icon">📱</span> Delete for me
            </button>
            <button onClick={() => setView("main")}>Cancel</button>
          </div>
        )}

        {view === "info" && (
          <div className="sheet-list">
            <div className="sheet-title">Message info</div>
            <dl className="sheet-info">
              {own ? (
                <>
                  <dt>Sent</dt>
                  <dd>{m.status === "queued" ? "Waiting to send" : fmtTime(m.createdAt)}</dd>
                  <dt>Delivered</dt>
                  <dd>{fmtTime(m.deliveredAt)}</dd>
                  <dt>Read</dt>
                  <dd>{fmtTime(m.readAt)}</dd>
                </>
              ) : (
                <>
                  <dt>Sent</dt>
                  <dd>{fmtTime(m.createdAt)}</dd>
                  <dt>Received</dt>
                  <dd>{fmtTime(m.deliveredAt)}</dd>
                </>
              )}
              <dt>Disappears</dt>
              <dd>{m.expiresAt ? fmtTime(m.expiresAt) : "5 minutes after it's delivered"}</dd>
            </dl>
            <button onClick={() => setView("main")}>Back</button>
          </div>
        )}
      </div>
    </div>
  );
}
